// Operator route-overrides — flat NET flight price for specific pad→pad routes
// that REPLACES the automatic cost-model FLIGHT price. Pad fees (routePadFeesNet)
// run UNCHANGED on top — the override replaces ONLY the flight-price part.
//
// SHARED pure functions: server getRoutePriceFromDoc + client matchRouteOverride
// call the SAME code → zero drift (like routePadFeesNet). NET-ONLY — no markup
// here (brokerToCustomer is applied once downstream, identical for all sources).

const refId = (r) => (r && (r._ref || r.ref)) || r || null;

// Find the winning routeOverride for (fromId, toId, aircraftId), or null.
// Match: fromPads ∋ fromId AND toPads ∋ toId  (or, if bothDirections, the reverse).
//        aircraftRef empty → applies to ALL the operator's aircraft; set → only that one.
// Tiebreak (deterministic, 3-stage): most-specific (smallest fromPads×toPads)
//   → lastUpdatedAt desc → array index asc.
export function matchRouteOverride(overrides, fromId, toId, aircraftId) {
  if (!Array.isArray(overrides) || !fromId || !toId) return null;
  const matches = [];
  for (let i = 0; i < overrides.length; i++) {
    const o = overrides[i];
    if (!o || !(Number(o.oneWayNet) > 0)) continue;
    // aircraftRef = the booking-engine projection (aircraftRef->id); aircraftId =
    // the simulator projection name — both are the aircraft slug. Accept either so
    // the SAME resolver serves the engine and the sim (zero drift).
    const acRef = refId(o.aircraftRef) || o.aircraftId || null;
    if (acRef && acRef !== aircraftId) continue; // aircraft-specific entry → must match (incl. when no aircraft context)
    const from = (o.fromPads || []).map(refId).filter(Boolean);
    const to = (o.toPads || []).map(refId).filter(Boolean);
    const fwd = from.includes(fromId) && to.includes(toId);
    const rev = !!o.bothDirections && from.includes(toId) && to.includes(fromId);
    if (fwd || rev) matches.push({ o, idx: i, size: (from.length || 1) * (to.length || 1) });
  }
  if (!matches.length) return null;
  matches.sort((a, b) => {
    if (a.size !== b.size) return a.size - b.size;                 // 1) most specific
    const ta = Date.parse(a.o.lastUpdatedAt || '') || 0;
    const tb = Date.parse(b.o.lastUpdatedAt || '') || 0;
    if (ta !== tb) return tb - ta;                                 // 2) lastUpdatedAt desc
    return a.idx - b.idx;                                          // 3) array index asc
  });
  return matches[0].o;
}

// NET flight price from a matched override for the booking trip type.
//   one-way → oneWayNet
//   round-trip → roundTripNet if set, ELSE C additive-delta:
//       oneWayNet + (cmRTNet − cmOneWayNet)  (override one-way + the real return-leg
//       cost from the locked one-base-round geometry; RT < 2× one-way).
//       Fallback to oneWayNet × 2 ONLY when there is no cost-model geometry
//       (cmOneWayNet/cmRTNet null — e.g. a manual-only aircraft).
export function overrideFlightNet(override, isRoundTrip, cmOneWayNet = null, cmRTNet = null) {
  if (!override) return null;
  const ow = Number(override.oneWayNet);
  if (!(ow > 0)) return null;
  if (!isRoundTrip) return ow;
  const rt = Number(override.roundTripNet);
  if (rt > 0) return rt;
  if (cmOneWayNet != null && cmRTNet != null && Number(cmOneWayNet) > 0) {
    return ow + (Number(cmRTNet) - Number(cmOneWayNet)); // C additive-delta
  }
  return ow * 2; // no cost-model geometry → fall back to ×2
}
